/* eslint-disable react-refresh/only-export-components */
/**
 * Falcon SaaSShell Configuration
 *
 * This file contains all product-specific configuration for the SaaSShell.
 * App.tsx stays minimal - just providers and routing.
 */

import {
  AppWindow,
  AlertTriangle,
  Bell,
  BarChart3,
  Settings,
  Users,
  Zap,
  FileText,
  Globe,
  History,
} from 'lucide-react'
import type { SaaSShellConfig, NavItem } from '@roselabs-io/saas-shell'
import { authApi } from './lib/auth'
import { teamsApi } from './api/teams'

// Product Pages (Falcon-specific)
import DashboardPage from './pages/DashboardPage'
import AppsDashboardPage from './pages/AppsDashboardPage'
import AppDetailPage from './pages/AppDetailPage'
import ErrorsPage from './pages/ErrorsPage'
import ErrorDetailPage from './pages/ErrorDetailPage'
import AlertsPage from './pages/AlertsPage'
import AlertSettingsPage from './pages/AlertSettingsPage'
import ReportSettingsPage from './pages/ReportSettingsPage'
import StatusPagesPage from './pages/StatusPagesPage'
import MembersPage from './pages/MembersPage'
import SettingsPage from './pages/SettingsPage'
import PricingPage from './pages/PricingPage'
import AuditLogPage from './pages/AuditLogPage'

// =============================================================================
// Logo
// =============================================================================

export function FalconLogo() {
  return <img src="/falcon-logo.png" alt="Falcon" className="h-10 w-auto" />
}

// =============================================================================
// Navigation
// =============================================================================

export const navigation: NavItem[] = [
  { id: 'dashboard', path: '/t/:teamId/dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'errors', path: '/t/:teamId/errors', label: 'Errors', icon: AlertTriangle },
  { id: 'apps', path: '/t/:teamId/apps', label: 'Apps', icon: AppWindow },
  { id: 'alert-settings', path: '/t/:teamId/alert-settings', label: 'Alert Settings', icon: Bell },
  { id: 'reports', path: '/t/:teamId/reports', label: 'Reports', icon: FileText },
  { id: 'status-pages', path: '/t/:teamId/status-pages', label: 'Status Pages', icon: Globe },
  {
    id: 'audit-log',
    path: '/t/:teamId/audit-log',
    label: 'Audit Log',
    icon: History,
    minRole: 'admin',
  },
  { id: 'members', path: '/t/:teamId/members', label: 'Members', icon: Users, minRole: 'admin' },
  { id: 'settings', path: '/t/:teamId/settings', label: 'Settings', icon: Settings },
]

// =============================================================================
// SaaSShell Configuration
// =============================================================================

export const falconConfig: SaaSShellConfig = {
  productSlug: 'falcon',
  productName: 'Falcon',
  logo: <FalconLogo />,
  navigation,

  routes: [
    // Product-specific routes
    { path: '/t/:teamId/dashboard', element: <DashboardPage /> },
    { path: '/t/:teamId/errors', element: <ErrorsPage /> },
    { path: '/t/:teamId/apps', element: <AppsDashboardPage /> },
    { path: '/t/:teamId/apps/:appId/errors/:groupId', element: <ErrorDetailPage /> },
    { path: '/t/:teamId/apps/:id', element: <AppDetailPage /> },
    { path: '/t/:teamId/alerts', element: <AlertsPage /> },
    { path: '/t/:teamId/alert-settings', element: <AlertSettingsPage /> },
    { path: '/t/:teamId/reports', element: <ReportSettingsPage /> },
    { path: '/t/:teamId/status-pages', element: <StatusPagesPage /> },
    { path: '/t/:teamId/audit-log', element: <AuditLogPage /> },
    { path: '/t/:teamId/members', element: <MembersPage /> },
    { path: '/t/:teamId/settings', element: <SettingsPage /> },
    { path: '/t/:teamId/pricing', element: <PricingPage /> },
  ],

  oauth: { google: true, github: true },

  activation: {
    description: 'Activate your account to start monitoring your applications with Falcon.',
    features: [
      { text: 'Real-time error tracking', icon: <Zap className="w-4 h-4 text-primary-400" /> },
      { text: 'Performance monitoring', icon: <Zap className="w-4 h-4 text-primary-400" /> },
      { text: 'Alerts & integrations', icon: <Zap className="w-4 h-4 text-primary-400" /> },
    ],
    activateProduct: (productSlug: string) => authApi.activateProduct(productSlug),
    getProductStatus: () => authApi.getProductStatus('falcon'),
  },

  teamApi: {
    getInviteDetails: teamsApi.getInviteDetails,
    acceptInvite: teamsApi.acceptInvite,
    acceptOwnershipTransfer: teamsApi.acceptOwnershipTransfer,
    createTeam: async (name: string) => teamsApi.createTeam({ name }),
  },

  securityApi: {
    securityConfirm: authApi.securityConfirm,
    securityDeny: authApi.securityDeny,
    deviceConfirm: authApi.deviceConfirm,
    deviceReport: authApi.deviceReport,
  },

  paths: {
    teamDashboard: '/t/:teamId/dashboard',
    pricing: '/t/:teamId/pricing',
    home: '/login', // No landing page, logo stays on login
  },

  showBillingBanner: true,
}
